import { whenEditorLive } from '@heidi/editor/core';
import type { Transaction } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import { debounce } from 'lodash-es';

/**
 * Transaction meta stamped on the highlight transaction itself so the
 * subscription below does not treat the hook's own dispatch as a user edit.
 */
export const NOTE_EVIDENCE_APPLY_META = 'noteEvidenceApply';

/**
 * Edits re-apply highlights only after this pause. Re-applying walks the whole
 * document and dispatches a second transaction, so doing it per keystroke was a
 * main-thread cost paid on every edit while evidence was showing.
 */
export const NOTE_EVIDENCE_REAPPLY_DEBOUNCE_MS = 300;

/**
 * Re-runs `reapply` after the user pauses editing. Shared by both note-evidence
 * twins so the debounce window, the own-transaction skip and the destroy guard
 * cannot drift between them.
 *
 * Returns the unsubscribe; it drops any pending re-apply before detaching.
 */
export function subscribeNoteEvidenceReapply(
  editor: Editor,
  callSite: string,
  reapply: () => void
): () => void {
  const debounced = debounce(() => {
    whenEditorLive(editor, callSite, () => reapply());
  }, NOTE_EVIDENCE_REAPPLY_DEBOUNCE_MS);

  const onUpdate = ({ transaction }: { transaction: Transaction }) => {
    if (transaction.getMeta(NOTE_EVIDENCE_APPLY_META)) return;
    debounced();
  };

  editor.on('update', onUpdate);

  return () => {
    debounced.cancel();
    editor.off('update', onUpdate);
  };
}
